import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { useToast } from '../core/hooks/useToast';
import { requestPasswordReset, resetPassword as authServiceResetPassword, login as authServiceLogin, register as authServiceRegister } from '../services/authService';

// Helper function to get cookie value by name
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
};

interface IUser {
  email: string;
  fullName: string;
}

interface IAuthContext {
  user: IUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  authToken: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (firstName: string, lastName: string, email: string, password: string) => Promise<any>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (email: string, newPassword: string, resetToken: string) => Promise<void>;
  getAuthRedirectUrl: () => string | null;
}

const AuthContext = createContext<IAuthContext | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<IUser | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { success, error: showError } = useToast();

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await authServiceLogin(email, password);
      setUser(response.user || null);

      const token = response.token || getCookie('authToken') || getCookie('token');
      if (!token) {
        throw new Error('Authentication token not found. Please try logging in again.');
      }
      setAuthToken(token);

      success('Login successful');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed. Please try again.';
      setError(errorMessage);
      showError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [success, showError]);

  const register = useCallback(async (firstName: string, lastName: string, email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await authServiceRegister(firstName, lastName, email, password);
      success('Registration successful');
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Registration failed. Please try again.';
      setError(errorMessage);
      showError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [success, showError]);

  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await axios.post(`api/auth/logout`);
      setUser(null);
      setAuthToken(null);
      success('Logged out successfully');
    } catch (err) {
      console.error('Logout error:', err);
      // Even if logout fails, clear the user state
      setUser(null);
      setAuthToken(null);
    } finally {
      setIsLoading(false);
    }
  }, [success]);

  const handleForgotPassword = async (email: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await requestPasswordReset(email);
      // Always show generic message to avoid leaking user existence
      success('If an account exists for this email, you will receive a password reset link.');
    } catch (err) {
      // Even on error, show generic message
      success('If an account exists for this email, you will receive a password reset link.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (email: string, newPassword: string, resetToken: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await authServiceResetPassword(resetToken, newPassword);
      success('Password has been reset successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset password';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getAuthRedirectUrl = useCallback(() => {
    if (!authToken) return null;

    const baseUrl = process.env.REACT_APP_LEGACY_BASE || process.env.REACT_APP_FRONTEND_URL || 'https://demo.eureekabi.com';

    return `${baseUrl}/auth/bridge?token=${authToken}`;
  }, [authToken]);

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    authToken,
    login,
    register,
    logout,
    forgotPassword: handleForgotPassword,
    resetPassword: handleResetPassword,
    getAuthRedirectUrl,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 